// The question card (SPEC 7.4). Identity stays visible while scrolling; one card open at a time;
// buttons lock for a second when the prompt changes; choices are full width and say what they do.
import type { Card, Choice, Question } from "@whistle/protocol";
import { useEffect, useMemo, useRef, useState } from "react";
import { useApp, useAppState } from "../app/AppContext";
import { navigate } from "../app/router";
import { useNow } from "../app/useNow";
import { answersComplete, buildAnswersCommand, buildChoiceCommand, remainingChars, visibleChoices, type AnswerSet, type OtherSet } from "../lib/commands";
import { answerBlocker, choiceEffect, isAnswerable, promptState, readOnlyNote, requestText, sendsForCard, showsTurnEnded, TURN_ENDED_BODY, TURN_ENDED_TITLE, type Blocker } from "../lib/model";
import { clock, countdown, duration } from "../lib/time";
import { Banner } from "../components/Banner";
import { CommandText, hasMarkers } from "../components/CommandText";
import { EffectMark } from "../components/EffectMark";
import { IconArrow } from "../components/Icons";
import { ResultView } from "../components/ResultView";
import { Sheet } from "../components/Sheet";
import { Page, TabBar, TopBar } from "../components/Shell";
import { cardVisual, StateChip } from "../components/StateChip";
import { kindLabel } from "./Inbox";
import { StatusBanners } from "./Inbox";

export function CardScreen({ requestId }: { requestId: string }) {
  const s = useAppState();
  const card = s.cards.find((c) => c.requestId === requestId);
  const sends = sendsForCard(s, requestId);
  const now = useNow();
  if (!card) {
    const last = sends[0];
    return (
      <>
        <TopBar back="/" title="Inbox" />
        <Page>
          <div className="empty">
            <h2>No longer waiting.</h2>
            <p>{last ? `${last.agentName} isn't asking this any more.` : "This question isn't on the list any more. It may have been answered at the desk, or the agent moved on."}</p>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate("/", { replace: true })}>
              Back to the inbox
            </button>
          </div>
          {last && <ResultView rec={last} now={now} />}
        </Page>
        <TabBar />
      </>
    );
  }
  return <OpenCard key={card.requestId} card={card} now={now} />;
}

function OpenCard({ card, now }: { card: Card; now: number }) {
  const s = useAppState();
  const { controller } = useApp();
  const [showFull, setShowFull] = useState(false);
  const [explaining, setExplaining] = useState(false);
  const [text, setText] = useState("");
  const [answers, setAnswers] = useState<AnswerSet>({});
  const [other, setOther] = useState<OtherSet>({});
  const [oldDraft, setOldDraft] = useState<string | null>(null);
  const prevHash = useRef(card.promptHash);
  const liveRef = useRef<HTMLDivElement>(null);

  const blocker = answerBlocker(s, card, now);
  const locked = blocker?.code === "updated";
  const machine = s.machine?.name ?? s.pairing?.machine.name ?? "your PC";
  const choices = useMemo(() => visibleChoices(card), [card]);
  const hiddenRisky = card.choices.length - choices.length;
  const denyChoice = choices.find((c) => c.effect === "deny");
  const sends = sendsForCard(s, card.requestId);
  const notSent = s.notSent[card.requestId];
  const released = s.releasedCards[card.requestId];
  const isQuestion = card.kind === "question" && !!card.questions?.length;
  const answerable = isAnswerable(card);
  const standing = promptState(card, s.sends);
  const note = readOnlyNote(card);
  const request = requestText(card);
  const blockedId = `blocked-${card.requestId}`;
  const blockedLine = blocker && blocker.code !== "updated" ? blockedText(blocker) : null;
  const textOff = answerable && !card.allowsText && (s.settings ? !s.settings.textReplies : true);

  // Drafts follow the original question: a changed prompt gets a fresh draft and the old one is kept.
  useEffect(() => {
    let alive = true;
    void controller.loadDraft(card).then((d) => {
      if (!alive || !d) return;
      if (d.text) {
        setText(d.text);
        setExplaining(true);
      }
      if (d.answers) setAnswers(d.answers);
      if (d.other) setOther(d.other);
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.requestId, card.promptHash]);

  useEffect(() => {
    if (prevHash.current !== card.promptHash) {
      const hadDraft = text.trim() !== "" || Object.values(other).some((t) => t.trim()) || Object.values(answers).some((a) => a.length);
      if (hadDraft) setOldDraft("The question changed. Your reply is saved but wasn't sent. It stays with the old question.");
      prevHash.current = card.promptHash;
      setText("");
      setAnswers({});
      setOther({});
      setExplaining(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.promptHash]);

  useEffect(() => {
    const t = setTimeout(() => void controller.saveDraft(card, { text, answers, other }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, answers, other, card.requestId, card.promptHash]);

  const stampNow = () => {
    const st = controller.store.getState().stamp;
    if (!st) throw new Error("No fresh state from your PC.");
    return st;
  };

  const choose = (choiceId: string, withText?: string) => {
    const choice = choices.find((c) => c.id === choiceId);
    const label = choice?.label ?? "Answer";
    const summary = withText?.trim() ? `${label} and explain` : label;
    controller.answer(card, () => buildChoiceCommand(card, stampNow(), choiceId, withText), summary, withText, choiceEffect(choice?.effect));
  };
  const submitAnswers = () => {
    const n = card.questions?.length ?? 0;
    controller.answer(card, () => buildAnswersCommand(card, stampNow(), answers, other), n === 1 ? "1 answer" : `${n} answers`, Object.values(other).filter(Boolean).join("\n") || undefined, "answer");
  };

  // Allow and Deny look clearly different (symbol, tint, word) at the same size and weight, and every deny sits below
  // a gap: touches tend to land low, so a slip from Allow falls into the gap, and a slip from Deny can't reach Allow.
  const describedBy = blockedLine ? blockedId : undefined;
  const offered = choices.filter((c) => c.effect !== "deny" || !explaining);
  const upper = offered.filter((c) => choiceEffect(c.effect) !== "deny");
  const lower = offered.filter((c) => choiceEffect(c.effect) === "deny");
  const canExplain = !!denyChoice && card.allowsText;
  const hasLower = lower.length > 0 || canExplain;
  const choiceButton = (c: Choice) => {
    const effect = choiceEffect(c.effect);
    const where = c.keyHint ? `sends ${c.keyHint}` : `goes to ${card.agentName}`;
    return (
      <button key={c.id} type="button" className={`choice choice-${effect}`} disabled={!!blocker} aria-label={`${c.label}: ${where}`} aria-describedby={describedBy} onClick={() => choose(c.id)}>
        <EffectMark effect={effect} />
        <span className="choice-label">{c.label}</span>
        {c.keyHint ? (
          <small>
            sends <span className="key">{c.keyHint}</span>
          </small>
        ) : (
          <small>goes to {card.agentName}</small>
        )}
      </button>
    );
  };

  const over = remainingChars(text) < 0;
  const canSubmitAnswers = isQuestion && answersComplete(card, answers, other) && !blocker && !over;

  return (
    <>
      <TopBar back="/" title="Inbox" />
      <Page className="card">
        <StatusBanners s={s} now={now} />
        <header className="identity">
          <div className="top">
            <div className="who">
              <b>{card.agentName}</b>
              <span>{kindLabel(card.agentKind)}</span>
            </div>
            {locked ? <span className="updated-tag" role="status">Updated</span> : <StateChip state={cardVisual(card, s.sends)} />}
          </div>
          <dl className="meta">
            {card.project && (
              <>
                <dt>Project</dt>
                <dd>{card.project}</dd>
              </>
            )}
            <dt>Machine</dt>
            <dd>
              {machine} · {card.envLabel}
            </dd>
            <dt>Waiting</dt>
            <dd>
              <span className="num">{duration(now - card.createdAt)}</span> · checked <span className="num">{clock(card.checkedAt)}</span>
            </dd>
          </dl>
        </header>

        {card.holdUntil && card.holdUntil > now && (
          <Banner icon="info" title="Waiting for your phone.">
            Goes back to your desk in <span className="num">{countdown(card.holdUntil - now)}</span>.
          </Banner>
        )}
        {released && (
          <Banner icon="info" title="Moved to your desk.">
            A typed reply isn't available here.
          </Banner>
        )}
        {showsTurnEnded(card, s.sends) && (
          <Banner icon="info" title={TURN_ENDED_TITLE}>
            {TURN_ENDED_BODY}
          </Banner>
        )}
        {oldDraft && (
          <Banner tone="warn" icon="alert" title="The question changed." onDismiss={() => setOldDraft(null)}>
            {oldDraft}
          </Banner>
        )}

        {/* Only a card the daemon says is at the desk sends the user there. An answer on its way shows its result instead,
            and a card that isn't open yet says so without inviting keystrokes at the terminal (or, paused, says that). */}
        {standing === "desk" && note && (
          <Banner icon="info" title={note}>
            Answer it at your desk. This phone can't send anything for it.
          </Banner>
        )}
        {standing === "pending" && note && blocker?.code !== "paused" && (
          <Banner icon="info" title={note}>
            Answering opens here when your PC is ready for it.
          </Banner>
        )}

        <p className="question">{card.title}</p>

        {request && (
          <>
            <CommandText text={request} label={card.source === "screen" ? "Screen text" : "Request"} />
            {(hasMarkers(request) || card.detailFull) && (
              <div className="cmd-note">
                {hasMarkers(request) && <span>Shortened or masked for display.</span>}
                {card.detailFull && (
                  <button type="button" className="textlink" onClick={() => setShowFull(true)}>
                    {card.source === "screen" ? "Show screen" : "Show full request"}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {notSent && (
          <Banner tone="warn" icon="alert" title={notSent.reason} onDismiss={() => controller.store.dispatch({ type: "clearNotSent", requestId: card.requestId })}>
            {/changed/i.test(notSent.reason) ? "Read the new question and tap again." : "Nothing left your phone. Tap again."}
          </Banner>
        )}

        {sends.slice(0, 1).map((rec) => (
          <ResultView key={rec.commandId} rec={rec} now={now} />
        ))}

        {/* An answerable card says why it's blocked beside its buttons instead (blockedLine), and a read-only approval or
            question has its note banner above. */}
        {blocker && !answerable && blocker.code !== "updated" && blocker.code !== "in-flight" && !(blocker.code === "read-only" && standing !== null) && (
          <Banner tone={blocker.code === "read-only" ? "info" : "warn"} icon={blocker.code === "read-only" ? "info" : blocker.code === "paused" || blocker.code === "removed" ? "lock" : "cloud-off"} title={blocker.text}>
            {blocker.code === "read-only" ? (card.kind === "maybe" ? "herdr can't confirm this is a prompt, and no keys are sent for it." : "This prompt isn't one the daemon can answer with keys. Nothing is sent.") : blocker.code === "stale" ? "Answering opens once a fresh state arrives." : "Answering is off."}
          </Banner>
        )}

        {answerable && (
          <div ref={liveRef} className={locked ? "locked" : undefined} aria-busy={locked}>
            {blockedLine && (
              <p className="choices-blocked" id={blockedId} role="status">
                {blockedLine}
              </p>
            )}
            {isQuestion ? (
              <div className="qs">
                {card.questions!.map((q) => (
                  <QuestionBlock key={q.id} q={q} card={card} answers={answers} other={other} disabled={!!blocker} onAnswers={setAnswers} onOther={setOther} />
                ))}
                <button type="button" className="btn btn-primary btn-block" disabled={!canSubmitAnswers} aria-describedby={blockedLine ? blockedId : undefined} onClick={submitAnswers}>
                  Answer {card.questions!.length === 1 ? "this question" : `all ${card.questions!.length} questions`}
                  <IconArrow />
                </button>
                <p className="choices-note">All answers go to {card.agentName} in one message.</p>
              </div>
            ) : (
              <div className="choices" role="group" aria-label="Choices">
                {upper.map(choiceButton)}
                {upper.length > 0 && hasLower && <div className="choices-gap" aria-hidden="true" />}
                {lower.map(choiceButton)}
                {denyChoice && canExplain && !explaining && (
                  <button type="button" className="choice choice-deny" disabled={!!blocker} aria-label={`${denyChoice.label} and explain: type a reason`} aria-describedby={describedBy} onClick={() => setExplaining(true)}>
                    <EffectMark effect="deny" />
                    <span className="choice-label">{denyChoice.label} and explain</span>
                    <small>type a reason</small>
                  </button>
                )}
                {denyChoice && canExplain && explaining && (
                  <div className="reply">
                    <label htmlFor="reply-text" className="label">
                      {denyChoice.label} and explain
                    </label>
                    <textarea id="reply-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="No, do this instead…" disabled={!!blocker} autoFocus />
                    <div className="hint">
                      <span>Feedback for {card.agentName}. It may not follow it.</span>
                      <span className={over ? "over" : undefined}>{remainingChars(text).toLocaleString()} left</span>
                    </div>
                    <div className="btn-row">
                      <button type="button" className="btn btn-ghost" onClick={() => setExplaining(false)}>
                        Back
                      </button>
                      <button type="button" className="btn btn-deny" disabled={!!blocker || over || !text.trim()} onClick={() => choose(denyChoice.id, text)}>
                        <EffectMark effect="deny" />
                        {denyChoice.label} and send
                      </button>
                    </div>
                    <p className="draft-kept">Your text is kept on this phone until it's sent.</p>
                  </div>
                )}
                {textOff && (
                  <p className="choices-note">
                    Typed replies are off on {machine}. Turn them on at your desk with <code>whistle text replies on</code>.
                  </p>
                )}
                {hiddenRisky > 0 && (
                  <p className="choices-note">
                    {hiddenRisky} choice{hiddenRisky === 1 ? "" : "s"} hidden: {hiddenRisky === 1 ? "it" : "they"} would widen what the agent may do. <code>whistle risky-choices on</code> at the desk shows {hiddenRisky === 1 ? "it" : "them"}.
                  </p>
                )}
                <p className="choices-note">Each choice goes to {card.agentName} only. Nothing is sent until the 2-second count ends.</p>
              </div>
            )}
          </div>
        )}

        {sends.slice(1).map((rec) => (
          <ResultView key={rec.commandId} rec={rec} now={now} />
        ))}
      </Page>
      <TabBar />
      <Sheet open={showFull} title={card.source === "screen" ? "Screen text" : "Full request"} onClose={() => setShowFull(false)}>
        <p className="small muted">
          {card.source === "screen" ? "The screen as herdr saw it, with escape codes removed." : card.kind === "approval" ? "Every field of the request, exactly what Allow authorises. Hidden characters and possible credentials are marked; nothing else is changed." : "The original, as the agent sent it."}
        </p>
        <CommandText text={card.detailFull ?? card.detail} label="Full request" plain />
      </Sheet>
    </>
  );
}

/** The reason beside disabled choices, so they never look tappable while doing nothing. */
function blockedText(b: Blocker): string {
  switch (b.code) {
    case "disconnected":
      return "Reconnecting…";
    case "stale":
      return "Checking the question is still waiting…";
    default:
      return b.text;
  }
}

function QuestionBlock({ q, card, answers, other, disabled, onAnswers, onOther }: { q: Question; card: Card; answers: AnswerSet; other: OtherSet; disabled: boolean; onAnswers: (a: AnswerSet) => void; onOther: (o: OtherSet) => void }) {
  const picked = answers[q.id] ?? [];
  const toggle = (optId: string) => {
    if (q.multiSelect) onAnswers({ ...answers, [q.id]: picked.includes(optId) ? picked.filter((x) => x !== optId) : [...picked, optId] });
    else onAnswers({ ...answers, [q.id]: [optId] });
  };
  const name = `q-${card.requestId}-${q.id}`;
  return (
    <fieldset className="q" disabled={disabled} style={{ border: 0, padding: 0, margin: 0 }}>
      <header>
        {q.header && <span className="label">{q.header}</span>}
        <legend className="text">{q.text}</legend>
        <span className="small muted">{q.multiSelect ? "Pick any that apply." : "Pick one."}</span>
      </header>
      <div className="opts">
        {q.options.map((o) => (
          <label key={o.id} className="opt">
            <input type={q.multiSelect ? "checkbox" : "radio"} name={name} value={o.id} checked={picked.includes(o.id)} onChange={() => toggle(o.id)} />
            <span className="l">
              <b>{o.label}</b>
              {o.description && <span>{o.description}</span>}
            </span>
          </label>
        ))}
      </div>
      {card.allowsText && (
        <div className="other">
          <label htmlFor={`${name}-other`} className="small muted">
            Other (typed answer)
          </label>
          <input id={`${name}-other`} type="text" value={other[q.id] ?? ""} onChange={(e) => onOther({ ...other, [q.id]: e.target.value })} placeholder="Your own answer" />
        </div>
      )}
    </fieldset>
  );
}
